import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { Save, X, Loader2, ChevronDown, Cog, Monitor } from "lucide-react";
import DiscontinuedBadge from "@/components/DiscontinuedBadge";

/**
 * DeptMultiCell — compact multi-select popover for the Department column.
 *
 * Trigger shows the currently-picked departments as tiny chips (or "— none —").
 * Click to open a checkbox list of every department with quick "Tick all"
 * and "Clear" actions — handy for shared items like kitchen consumables
 * (tea/coffee/milk) that all three team kitchens need to see on Take Stock.
 */
const DeptMultiCell = ({ item, currentDepts, allDepartments, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = (name) => {
    const has = currentDepts.includes(name);
    onChange(has ? currentDepts.filter((d) => d !== name) : [...currentDepts, name]);
  };

  // Promote a linked dept to primary by moving it to index 0. Backend uses
  // departments[0] as the primary owner for Reports Stock Value snapshots.
  const promote = (name) => {
    if (!currentDepts.includes(name) || currentDepts[0] === name) return;
    onChange([name, ...currentDepts.filter((d) => d !== name)]);
  };

  const primaryDept = currentDepts[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={`quickview-dept-${item.id}`}
        onClick={() => setOpen((o) => !o)}
        className="qv-input text-left w-full flex items-center justify-between gap-1 cursor-pointer overflow-hidden"
      >
        <span className="flex gap-1 items-center flex-1 min-w-0 overflow-hidden whitespace-nowrap">
          {currentDepts.length === 0 ? (
            <span className="text-[#607087] italic">— none —</span>
          ) : (
            <>
              {currentDepts.slice(0, 2).map((d, i) => (
                <span
                  key={d}
                  title={i === 0 ? `${d} · Primary owner` : d}
                  className={`inline-flex items-center gap-0.5 text-[10px] font-bold border-2 border-black rounded px-1 max-w-[80px] truncate leading-tight ${
                    i === 0 ? "bg-[#FFE59A]" : "bg-[#DFEEF7]"
                  }`}
                >
                  {i === 0 && <span aria-hidden>★</span>}
                  {d}
                </span>
              ))}
              {currentDepts.length > 2 && (
                <span
                  title={currentDepts.slice(2).join(", ")}
                  className="inline-block text-[10px] font-bold border-2 border-black rounded px-1 bg-[#FFF7D6] leading-tight"
                >
                  +{currentDepts.length - 2}
                </span>
              )}
            </>
          )}
        </span>
        <ChevronDown size={12} strokeWidth={2.5} className="text-[#607087] shrink-0" />
      </button>
      {open && (
        <div
          data-testid={`quickview-dept-popover-${item.id}`}
          className="absolute z-30 top-full left-0 mt-1 w-64 bg-white border-2 border-black rounded-lg shadow-lg p-2 max-h-72 overflow-y-auto"
        >
          <div className="flex gap-1 mb-2 sticky top-0 bg-white pb-1 border-b border-black/10">
            <button
              type="button"
              data-testid={`quickview-dept-tick-all-${item.id}`}
              onClick={() =>
                onChange([
                  ...currentDepts,
                  ...(allDepartments || [])
                    .map((d) => d.name)
                    .filter((n) => !currentDepts.includes(n)),
                ])
              }
              className="text-[10px] font-bold border-2 border-black rounded px-2 py-0.5 bg-[#FFF7D6] flex-1"
            >
              Tick all
            </button>
            <button
              type="button"
              data-testid={`quickview-dept-clear-${item.id}`}
              onClick={() => onChange([])}
              className="text-[10px] font-bold border-2 border-black rounded px-2 py-0.5 bg-white flex-1"
            >
              Clear
            </button>
          </div>
          <div className="text-[10px] text-[#607087] px-1 pb-1 leading-tight">
            <b>★</b> = primary owner (Reports Stock-value). Tap ☆ on a linked
            dept to promote it.
          </div>
          {(allDepartments || []).map((d) => {
            const checked = currentDepts.includes(d.name);
            const isPrimary = checked && primaryDept === d.name;
            return (
              <div
                key={d.id}
                data-testid={`quickview-dept-opt-${item.id}-${d.name}`}
                className="flex items-center gap-2 text-xs py-1 px-1 rounded hover:bg-[#F4F8FB]"
              >
                <label className="flex items-center gap-2 flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#001740]"
                    checked={checked}
                    onChange={() => toggle(d.name)}
                  />
                  <span className="flex-1">{d.name}</span>
                </label>
                {checked && (
                  <button
                    type="button"
                    disabled={isPrimary}
                    data-testid={`quickview-dept-primary-${item.id}-${d.name}`}
                    onClick={() => promote(d.name)}
                    title={
                      isPrimary
                        ? "This is the primary owner"
                        : `Make ${d.name} the primary owner`
                    }
                    className={`text-xs font-bold rounded px-1.5 border-2 border-black ${
                      isPrimary
                        ? "bg-[#FFE59A] cursor-default"
                        : "bg-white hover:bg-[#FFF7D6]"
                    }`}
                  >
                    {isPrimary ? "★" : "☆"}
                  </button>
                )}
              </div>
            );
          })}
          {(!allDepartments || allDepartments.length === 0) && (
            <div className="text-xs text-[#607087] italic p-2">
              No departments set up yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * QuickViewInventory — a flat editable table of every catalog item.
 * Managers use this to bulk-fill missing fields (order contact, low-stock
 * threshold, reorder weeks, type, department) after a bulk import — without
 * opening the full StockFormDialog for each one.
 *
 * UX contract:
 *  - Type + Category use the admin-managed lists (/stock-types,
 *    /stock-categories) so no typos and everything shows up on filter chips.
 *    If the item already has a value that's not in the list (legacy import),
 *    it is injected as a first option so we never silently rewrite it.
 *  - Edits stay LOCAL until the user clicks Save (per row) → prevents
 *    stray keystrokes overwriting good data mid-scroll.
 *  - Each row is independent; saving one row does not disturb others.
 *  - Toast on success/failure; the parent's `onSaved` re-fetches so
 *    downstream views (Reports, Dashboard) stay in sync.
 */
const QuickViewInventory = ({ items, users, onSaved }) => {
  const { departments } = useDepartments();
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: t }, { data: c }] = await Promise.all([
          api.get("/stock-types"),
          api.get("/stock-categories"),
        ]);
        setTypeOptions((t || []).map((x) => x.name).filter(Boolean));
        setCategoryOptions((c || []).map((x) => x.name).filter(Boolean));
      } catch {
        // Non-fatal — dropdowns just render empty, user can still save
        // via the full StockFormDialog.
      }
    })();
  }, []);

  const usersSorted = useMemo(
    () => [...(users || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [users],
  );

  const patch = useCallback((id, field, value) => {
    setDrafts((d) => ({
      ...d,
      [id]: { ...(d[id] || {}), [field]: value },
    }));
  }, []);

  const cancel = (id) =>
    setDrafts((d) => {
      const n = { ...d };
      delete n[id];
      return n;
    });

  const save = async (item) => {
    const patchObj = drafts[item.id] || {};
    if (Object.keys(patchObj).length === 0) return;
    // Normalise types before sending.
    const body = { ...patchObj };
    if ("low_stock_threshold" in body)
      body.low_stock_threshold = Number(body.low_stock_threshold) || 0;
    if ("reorder_weeks" in body) body.reorder_weeks = Number(body.reorder_weeks) || 0;
    // `departments` arrives already as an array from DeptMultiCell — send
    // it through verbatim so multi-dept items (e.g. shared kitchen items)
    // keep every selected department.
    if ("order_contact_id" in body && !body.order_contact_id) {
      body.order_contact_id = null;
    }
    setSavingId(item.id);
    try {
      await api.put(`/stock/${item.id}`, body);
      toast.success(`Saved "${item.name}"`);
      cancel(item.id);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSavingId(null);
    }
  };

  // Move — convert this inventory line into a different register (Asset
  // / Machinery). Handy for one-off items accidentally logged as stock
  // that are really a physical asset or a machine. Source row is deleted
  // server-side, so we refresh the grid via the parent's onSaved.
  const moveTo = async (item, targetKind) => {
    const label = targetKind === "asset" ? "Assets" : "Machinery";
    if (
      !window.confirm(
        `Move "${item.name}" into ${label}?\n\nThis will:\n • create a new record in ${label} with the same name/category/cost/photos\n • delete the current Inventory line\n\nAny stock movements linked to this item stay in History but the item itself moves.`,
      )
    )
      return;
    try {
      await api.post("/records/move", {
        source_kind: "stock",
        source_id: item.id,
        target_kind: targetKind,
      });
      toast.success(`Moved "${item.name}" to ${label}`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not move record");
    }
  };

  const val = (item, field) =>
    drafts[item.id]?.[field] !== undefined
      ? drafts[item.id][field]
      : item[field] ?? "";

  const currentDepts = (item) => {
    const d = drafts[item.id]?.departments;
    if (d !== undefined) return d;
    if (item.departments?.length) return item.departments;
    return item.department ? [item.department] : [];
  };

  if (!items?.length) {
    return (
      <div className="nb-card p-10 text-center text-sm text-[#607087]" data-testid="quickview-empty">
        No items match your filters.
      </div>
    );
  }

  return (
    <div
      className="nb-card p-0 overflow-x-auto"
      data-testid="quickview-inventory-table"
    >
      <table className="min-w-[1200px] w-full text-sm">
        <thead className="bg-[#F4F8FB] border-b-2 border-black">
          <tr className="text-left">
            <th className="p-3 label-eyebrow w-[22%]">Item</th>
            <th className="p-3 label-eyebrow w-[10%]">Type</th>
            <th className="p-3 label-eyebrow w-[12%]">Category</th>
            <th className="p-3 label-eyebrow w-[12%]">Department</th>
            <th className="p-3 label-eyebrow w-[14%]">Order contact</th>
            <th className="p-3 label-eyebrow w-[8%]">Low-stock</th>
            <th className="p-3 label-eyebrow w-[8%]">Reorder&nbsp;wk</th>
            <th className="p-3 label-eyebrow w-[6%] text-right">Qty</th>
            <th className="p-3 label-eyebrow w-[8%] text-right sticky right-0 bg-[#F4F8FB]">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const dirty = !!drafts[it.id] && Object.keys(drafts[it.id]).length > 0;
            const busy = savingId === it.id;
            return (
              <tr
                key={it.id}
                data-testid={`quickview-row-${it.id}`}
                className={`border-b border-black/10 ${dirty ? "bg-[#FFF7DB]" : ""}`}
              >
                <td className="p-3 align-top">
                  <input
                    data-testid={`quickview-name-${it.id}`}
                    className="qv-input font-semibold"
                    value={val(it, "name") ?? ""}
                    onChange={(e) => patch(it.id, "name", e.target.value)}
                  />
                  <div className="text-[10px] text-[#607087] mt-0.5">
                    {it.sku ? `SKU ${it.sku}` : "no SKU"}
                    {it.supplier_default ? ` • ${it.supplier_default}` : ""}
                  </div>
                  {it.discontinued && (
                    <div className="mt-1">
                      <DiscontinuedBadge item={it} compact />
                    </div>
                  )}
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-type-${it.id}`}
                    className="qv-input"
                    value={val(it, "type") || ""}
                    onChange={(e) => patch(it.id, "type", e.target.value)}
                  >
                    <option value="">— pick —</option>
                    {val(it, "type") &&
                      !typeOptions.includes(val(it, "type")) && (
                        <option value={val(it, "type")}>
                          {val(it, "type")} (legacy)
                        </option>
                      )}
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-category-${it.id}`}
                    className="qv-input"
                    value={val(it, "category") || ""}
                    onChange={(e) => patch(it.id, "category", e.target.value)}
                  >
                    <option value="">— pick —</option>
                    {val(it, "category") &&
                      !categoryOptions.includes(val(it, "category")) && (
                        <option value={val(it, "category")}>
                          {val(it, "category")} (legacy)
                        </option>
                      )}
                    {categoryOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top">
                  <DeptMultiCell
                    item={it}
                    currentDepts={currentDepts(it)}
                    allDepartments={departments}
                    onChange={(next) => patch(it.id, "departments", next)}
                  />
                </td>
                <td className="p-2 align-top">
                  <select
                    data-testid={`quickview-contact-${it.id}`}
                    className="qv-input"
                    value={val(it, "order_contact_id") || ""}
                    onChange={(e) =>
                      patch(it.id, "order_contact_id", e.target.value)
                    }
                  >
                    <option value="">— unassigned —</option>
                    {usersSorted.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-threshold-${it.id}`}
                    className="qv-input font-mono-num"
                    type="number"
                    min="0"
                    step="1"
                    value={val(it, "low_stock_threshold")}
                    onChange={(e) =>
                      patch(it.id, "low_stock_threshold", e.target.value)
                    }
                  />
                </td>
                <td className="p-2 align-top">
                  <input
                    data-testid={`quickview-reorder-${it.id}`}
                    className="qv-input font-mono-num"
                    type="number"
                    min="0"
                    step="1"
                    value={val(it, "reorder_weeks")}
                    onChange={(e) =>
                      patch(it.id, "reorder_weeks", e.target.value)
                    }
                  />
                </td>
                <td className="p-3 align-top text-right font-mono-num">
                  {Number(it.current_qty || 0).toFixed(0)}{" "}
                  <span className="text-[10px] text-[#607087]">{it.unit}</span>
                </td>
                <td className="p-2 align-top text-right sticky right-0 bg-white">
                  {dirty ? (
                    <div className="inline-flex gap-1">
                      <button
                        data-testid={`quickview-save-${it.id}`}
                        onClick={() => save(it)}
                        disabled={busy}
                        className="nb-btn nb-btn-primary px-2 py-1.5 text-xs flex items-center gap-1"
                        title="Save this row"
                      >
                        {busy ? (
                          <Loader2 size={12} className="animate-spin" strokeWidth={2.5} />
                        ) : (
                          <Save size={12} strokeWidth={2.5} />
                        )}
                        Save
                      </button>
                      <button
                        data-testid={`quickview-cancel-${it.id}`}
                        onClick={() => cancel(it.id)}
                        disabled={busy}
                        className="nb-btn bg-white p-1.5"
                        title="Discard this row's edits"
                      >
                        <X size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  ) : (
                    <div className="inline-flex gap-1 items-center">
                      <button
                        data-testid={`quickview-move-asset-${it.id}`}
                        onClick={() => moveTo(it, "asset")}
                        className="nb-btn bg-[#FFF7D6] px-2 py-1 text-[10px] flex items-center gap-1"
                        title="Convert this line into an Asset record"
                      >
                        <Monitor size={10} strokeWidth={2.75} /> → Asset
                      </button>
                      <button
                        data-testid={`quickview-move-machine-${it.id}`}
                        onClick={() => moveTo(it, "machine")}
                        className="nb-btn bg-[#FFF7D6] px-2 py-1 text-[10px] flex items-center gap-1"
                        title="Convert this line into a Machinery record"
                      >
                        <Cog size={10} strokeWidth={2.75} /> → Machine
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default QuickViewInventory;
